import moment from 'moment-timezone'
import { IDateRangePoint } from '../types'

const isCurrentYear = (date: moment.Moment) =>
  date.isValid() ? date.isSame(moment(), 'year') : false

export interface IFormatDateTimeRangeArgs {
  startAt: IDateRangePoint
  endAt?: IDateRangePoint
  /**
   * @default false
   * Show the day or use 'Today' if date is today's date
   */
  showTodayText?: boolean
}

/**
 * Returns formatted time range string with timezone
 * @example getFormattedTimeRange({start}) // 'Fri 10/23/2020 16:30 CDT'
 * @example getFormattedTimeRange({start, end}) // 'Sun 10/23 16:30 – 19:30 CDT'
 */
export const getFormattedTimeRange = ({
  endAt,
  showTodayText = false,
  startAt,
}: IFormatDateTimeRangeArgs) => {
  const start = moment.tz(startAt.datetime, startAt.timezone)
  const end = endAt ? moment.tz(endAt?.datetime, endAt?.timezone) : moment.invalid()

  const { hasTime: startAtHasTime = true } = startAt
  const { hasTime: endAtHasTime = true } = endAt || {}

  const isInvalid = !start.isValid() && !end.isValid()
  const isSameDay = start.isSame(end, 'day')
  const isSameTimezone = start.format('z') === end.format('z') && endAtHasTime === startAtHasTime
  const showStartDateTimezone = (!isSameTimezone || !end.isValid()) && startAtHasTime

  if (isInvalid) return ''

  let startFormat: string = 'ddd MM/DD'
  const startIsToday = start.isSame(moment(), 'day')
  // use 'Today' if starts on today's date
  if (showTodayText && startIsToday) {
    startFormat = '[Today] MM/DD'
  }
  // append year if not current year
  if (!isCurrentYear(start)) {
    startFormat = `${startFormat}/YYYY`
  }
  // append time
  if (startAtHasTime) {
    startFormat = `${startFormat} HH:mm`
  } else {
    startFormat = `${startFormat} [TBD]`
  }
  // append timezone
  if (showStartDateTimezone) {
    startFormat = `${startFormat} z`
  }

  let endFormat: string = 'HH:mm z' // time and timezone only if end and start are on the same day
  if (!isSameDay) {
    endFormat = 'ddd MM/DD'
    const endIsToday = end.isSame(moment(), 'day')
    // use 'Today' if starts on today's date
    if (showTodayText && endIsToday) {
      endFormat = '[Today] MM/DD'
    }
    // append year if not current year
    if (!isCurrentYear(end)) {
      endFormat = `${endFormat}/YYYY`
    }
    // append time and timezone
    if (endAtHasTime) {
      endFormat = `${endFormat} HH:mm z`
    } else {
      endFormat = `${endFormat} [TBD]`
    }
  }

  const formattedStartDate = start.isValid() ? start.format(startFormat) : ''
  const formattedEndDate = end.isValid() ? end.format(endFormat) : ''

  return formattedEndDate ? [formattedStartDate, formattedEndDate].join(' – ') : formattedStartDate
}
